import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, FileSearch, XCircle, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth, SessionExpiredError } from "@/lib/auth-client";
import { Attachments } from "@/components/attachments";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TransactionStatusBadge } from "@/components/transaction-status-badge";
import { RFQ_STATUS_CONFIG } from "@/lib/status-config";
import { isConsumableRfq } from "@/lib/po-build";
import { canEditDraftRfq } from "@/lib/draft-edit-eligibility";
import { SubmitForApprovalButton } from "@/components/submit-for-approval-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import type { RFQDto, QuoteStatus } from "@procureflow/shared-types";

// US-018 — RFQ detail page (AUTO-Q2 sibling route file precedent
// matches purchase-requests.$id.tsx). Fetches GET /api/rfqs/:id via
// fetchWithAuth, which awaits mswReady transitively (auth-client.ts:
// 246-249 per ADR-016 Wave D) so the MSW mount-time fetch pattern
// (CLAUDE.md) is satisfied without a duplicate await here.
//
// Surface:
//   - Header: rfq_number, status Badge, submission deadline, source-
//     PR back-link (the canonical RFQ→PR link per US-018 AC; the
//     reverse PR→RFQ link remains DEFERRED).
//   - Vendor panel: company_name + quote_status badge ("Pending" if
//     null) + distributed_at timestamp per rfq_vendors row.
//   - Line items table: description, quantity, unit_of_measure.
//   - RBAC: ProcurementOfficer + Admin can view; Finance + Requester
//     see a forbidden message. The MSW + BE handlers also enforce 403
//     server-side — this guard is the UX cap, not the security
//     boundary.

export const Route = createFileRoute("/rfq/$id")({
  head: () => ({
    meta: [{ title: "RFQ Detail — ProcureFlow" }],
  }),
  // Same parent-vs-child pattern as the rfq.tsx list route (per Plan 04-03's
  // Outlet wiring). When a child like /rfq/$id/quotes/new is active, render
  // its Outlet instead of the detail page; otherwise render RFQDetailPage.
  component: function RFQDetailOrOutlet() {
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <RFQDetailPage />;
  },
});

function quoteStatusBadgeVariant(
  status: QuoteStatus | null,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Awarded":
      return "default";
    case "Submitted":
      return "secondary";
    case "Declined":
      return "destructive";
    case null:
    case undefined:
    default:
      return "outline";
  }
}

function RFQDetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const allowed = user?.role === "ProcurementOfficer" || user?.role === "Admin";

  const navigate = useNavigate();
  const [rfq, setRfq] = useState<RFQDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  // US-019: distribute confirmation Dialog state.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [distributing, setDistributing] = useState(false);
  // #3: delete-draft confirmation Dialog state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // US-064: close-RFQ (cancel remaining) Dialog state.
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // US-019: distribute the RFQ. POSTs /api/rfqs/:id/distribute, refetches
  // state from the 200 response body, and surfaces success/error via toast.
  // RBAC: the BE @Roles is `ProcurementOfficer`-only on this endpoint
  // (M3.B-shipped `apps/api/src/rfqs/rfqs.controller.ts`). The button is
  // gated client-side accordingly; the BE still enforces the boundary.
  async function handleDistribute() {
    if (!rfq) return;
    setDistributing(true);
    try {
      const res = await fetchWithAuth(`/api/rfqs/${rfq.id}/distribute`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body?.message ?? "Failed to distribute RFQ.");
        return;
      }
      const { data } = (await res.json()) as { data: RFQDto };
      setRfq(data);
      setConfirmOpen(false);
      toast.success("RFQ distributed");
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      toast.error("An unexpected error occurred.");
    } finally {
      setDistributing(false);
    }
  }

  // #3: delete a draft (Open) RFQ. DELETE releases the source PR's consumed
  // quantity (server-side); on success we leave the now-deleted detail page.
  async function handleDelete() {
    if (!rfq) return;
    setDeleting(true);
    try {
      const res = await fetchWithAuth(`/api/rfqs/${rfq.id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body?.message ?? "Failed to delete RFQ.");
        return;
      }
      setDeleteOpen(false);
      toast.success("RFQ deleted — its purchase request has been released.");
      navigate({ to: "/rfq" });
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      toast.error("An unexpected error occurred.");
    } finally {
      setDeleting(false);
    }
  }

  // US-064: close an RFQ by cancelling its remaining (un-consumed) quantity
  // without raising a PO. POSTs /api/rfqs/:id/close, refetches from the
  // response, surfaces success/error via toast. ProcurementOfficer/Admin only.
  async function handleClose() {
    if (!rfq) return;
    setClosing(true);
    setCloseError(null);
    try {
      const res = await fetchWithAuth(`/api/rfqs/${rfq.id}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(closeReason.trim() ? { reason: closeReason.trim() } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setCloseError(body?.message ?? "Failed to close the RFQ.");
        return;
      }
      const { data } = (await res.json()) as { data: RFQDto };
      setRfq(data);
      setCloseOpen(false);
      setCloseReason("");
      toast.success("RFQ closed");
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      setCloseError("An unexpected error occurred.");
    } finally {
      setClosing(false);
    }
  }

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setErrorStatus(null);
      try {
        // fetchWithAuth awaits mswReady internally per ADR-016 Wave D.
        const res = await fetchWithAuth(`/api/rfqs/${id}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setErrorStatus(res.status);
            setError(body?.message ?? "Failed to load RFQ.");
            setLoading(false);
          }
          return;
        }
        const { data } = (await res.json()) as { data: RFQDto };
        if (!cancelled) {
          setRfq(data);
          setLoading(false);
        }
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        if (!cancelled) {
          setError("An unexpected error occurred.");
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, allowed]);

  if (!allowed) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> RFQs
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          <p className="text-sm font-medium text-foreground">Access denied</p>
          <p className="text-sm text-muted-foreground">You do not have access to RFQs.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !rfq) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> RFQs
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          {errorStatus === 403 ? (
            <>
              <p className="text-sm font-medium text-foreground">Access denied</p>
              <p className="text-sm text-muted-foreground">You do not have access to RFQs.</p>
            </>
          ) : errorStatus === 404 ? (
            <>
              <p className="text-sm font-medium text-foreground">Not found</p>
              <p className="text-sm text-muted-foreground">
                This RFQ does not exist or has been removed.
              </p>
            </>
          ) : (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </div>
    );
  }

  // US-019: distribute action gates.
  //   - Send-to-Vendors button is visible only on Open RFQs to
  //     ProcurementOfficer (BE @Roles parity — Admin is NOT permitted on
  //     this endpoint in the M3.B-shipped controller).
  //   - Download PDF link is visible whenever the RFQ has been distributed
  //     (status !== 'Open'), for ProcurementOfficer + Admin (matches the
  //     BE PDF endpoint's @Roles which allows both).
  const canDistribute = rfq.status === "Open" && user?.role === "ProcurementOfficer";
  // #3: a draft (Open) RFQ can be deleted, releasing its source PR.
  const canDelete = rfq.status === "Open" && user?.role === "ProcurementOfficer";
  // US-077: a draft (Open) RFQ can be fully edited (header + lines, re-syncing the PR).
  const canEdit = canEditDraftRfq(rfq.status, user?.role);
  // US-057: an Open RFQ is submitted into its (flat) approval chain; vendor
  // distribution happens after approval.
  const canSubmitForApproval =
    rfq.status === "Open" && (user?.role === "ProcurementOfficer" || user?.role === "Admin");
  const canDownloadPdf =
    rfq.status !== "Open" && (user?.role === "ProcurementOfficer" || user?.role === "Admin");
  // US-064: an RFQ in a consumable state (Distributed / PartiallyConsumed) can
  // be closed — cancelling its remaining demand without raising a PO.
  const canClose =
    isConsumableRfq(rfq.status) && (user?.role === "ProcurementOfficer" || user?.role === "Admin");

  // US-021: Compare quotes link gate. RFQDto carries `quotes: QuoteDto[]`
  // — when at least one quote exists, render the navigation to the new
  // /rfq/$id/compare surface (AUTO-Q3).
  const hasQuotes = (rfq.quotes?.length ?? 0) >= 1;

  return (
    <div className="space-y-5">
      <Link
        to="/rfq"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> RFQs
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
                  <FileSearch className="h-3.5 w-3.5" />
                </div>
                <span className="font-mono text-sm font-medium text-muted-foreground">
                  {rfq.rfq_number}
                </span>
              </div>
              <TransactionStatusBadge config={RFQ_STATUS_CONFIG[rfq.status]} />
            </div>
            {/* Source PR back-link(s). For the legacy single-PR RFQ the header
                carries `purchase_request`; a multi-PR RFQ (US-063) has none and
                instead lists its distinct source PRs from `source_documents`. */}
            {rfq.purchase_request ? (
              <Link
                to="/purchase-requests/$id"
                params={{ id: rfq.purchase_request.id }}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <span className="font-mono text-xs">{rfq.purchase_request.pr_number}</span>
                {rfq.purchase_request.title && (
                  <span className="text-muted-foreground">— {rfq.purchase_request.title}</span>
                )}
              </Link>
            ) : rfq.source_documents.length > 0 ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Source PRs:</span>
                {rfq.source_documents.map((doc) => (
                  <Link
                    key={doc.id}
                    to="/purchase-requests/$id"
                    params={{ id: doc.id }}
                    className="font-mono text-xs text-primary hover:underline"
                  >
                    {doc.number}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
          <div className="shrink-0 text-right text-sm space-y-3">
            <div>
              <div className="text-xs text-muted-foreground">Submission deadline</div>
              <div className="font-medium tabular-nums">
                {formatDateTime(rfq.submission_deadline)}
              </div>
            </div>
            {(canDistribute ||
              canDownloadPdf ||
              hasQuotes ||
              canSubmitForApproval ||
              canClose ||
              canEdit ||
              canDelete) && (
              <div className="flex justify-end gap-2 flex-wrap items-start">
                {canSubmitForApproval && (
                  <SubmitForApprovalButton
                    endpoint={`/api/rfqs/${rfq.id}/submit`}
                    onSubmitted={(data) => setRfq(data as RFQDto)}
                  />
                )}
                {canEdit && (
                  <Button size="sm" variant="outline" data-testid="edit-rfq-button" asChild>
                    <Link to="/rfq/build" search={{ editId: rfq.id }}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Link>
                  </Button>
                )}
                {canDelete && (
                  <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="delete-rfq-button">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </Button>
                    </DialogTrigger>
                    <DialogContent dismissable={false}>
                      <DialogHeader>
                        <DialogTitle>Delete this draft RFQ?</DialogTitle>
                        <DialogDescription>
                          This permanently deletes the RFQ and releases the demand it reserved on
                          its source purchase request, making that PR available for a new RFQ. This
                          can't be undone.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setDeleteOpen(false)}
                          disabled={deleting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleDelete}
                          disabled={deleting}
                          data-testid="confirm-delete-rfq"
                        >
                          {deleting ? "Deleting…" : "Delete RFQ"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {hasQuotes && (
                  <Button asChild size="sm" variant="outline">
                    <Link to="/rfq/$id/compare" params={{ id: rfq.id }}>
                      Compare quotes
                    </Link>
                  </Button>
                )}
                {canDistribute && (
                  <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">Send to Vendors</Button>
                    </DialogTrigger>
                    <DialogContent dismissable={false}>
                      <DialogHeader>
                        <DialogTitle>Distribute this RFQ?</DialogTitle>
                        <DialogDescription>
                          This will mark the RFQ as Distributed and notify the selected vendors. You
                          won't be able to undo this.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setConfirmOpen(false)}
                          disabled={distributing}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleDistribute} disabled={distributing}>
                          {distributing ? "Distributing…" : "Confirm distribute"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {canDownloadPdf && (
                  <Button asChild size="sm" variant="outline">
                    <a href={`/api/rfqs/${rfq.id}/pdf`} download={`${rfq.rfq_number}.pdf`}>
                      Download PDF
                    </a>
                  </Button>
                )}
                {canClose && (
                  <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="close-rfq-button">
                        <XCircle className="h-3.5 w-3.5" /> Close RFQ
                      </Button>
                    </DialogTrigger>
                    <DialogContent dismissable={false}>
                      <DialogHeader>
                        <DialogTitle>Close this RFQ?</DialogTitle>
                        <DialogDescription>
                          This cancels all remaining (un-ordered) quantity on this RFQ without
                          raising a purchase order. Any quantity already consumed onto a PO is
                          unaffected. The RFQ will settle to Fully Consumed.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-1.5 text-left">
                        <label
                          htmlFor="rfq-close-reason"
                          className="text-xs font-medium text-foreground"
                        >
                          Reason (optional)
                        </label>
                        <textarea
                          id="rfq-close-reason"
                          value={closeReason}
                          onChange={(e) => setCloseReason(e.target.value)}
                          rows={3}
                          placeholder="Why is this RFQ being closed?"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
                        />
                        {closeError && <p className="text-xs text-destructive">{closeError}</p>}
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setCloseOpen(false)}
                          disabled={closing}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleClose}
                          disabled={closing}
                          data-testid="confirm-close-rfq-button"
                        >
                          {closing ? "Closing…" : "Confirm close"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold">Vendors</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Each vendor's distribution + quote status.
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Vendor</TableHead>
              <TableHead>Quote status</TableHead>
              <TableHead>Distributed at</TableHead>
              {/* US-020: Record Quote action column. ProcurementOfficer
                  only; entry hidden once a quote exists or RFQ is Closed. */}
              <TableHead className="w-40">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rfq.vendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                  No vendors invited to this RFQ.
                </TableCell>
              </TableRow>
            ) : (
              rfq.vendors.map((v) => (
                <TableRow key={v.vendor_id}>
                  <TableCell className="font-medium">{v.company_name}</TableCell>
                  <TableCell>
                    <Badge variant={quoteStatusBadgeVariant(v.quote_status)}>
                      {v.quote_status ?? "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {v.distributed_at ? formatDateTime(v.distributed_at) : "—"}
                  </TableCell>
                  <TableCell>
                    {v.quote_status === null &&
                      user?.role === "ProcurementOfficer" &&
                      rfq.status !== "Closed" && (
                        <Button asChild variant="outline" size="sm">
                          <Link
                            to="/rfq/$id/quotes/new"
                            params={{ id: rfq.id }}
                            search={{ vendorId: v.vendor_id }}
                          >
                            Record Quote
                          </Link>
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold">Line items</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead className="w-24">Qty</TableHead>
              <TableHead className="w-32">Unit of measure</TableHead>
              {/* US-063 — per-line provenance to the source PR. */}
              <TableHead className="w-32">Source PR</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rfq.line_items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                  No line items on this RFQ.
                </TableCell>
              </TableRow>
            ) : (
              rfq.line_items.map((li) => (
                <TableRow key={li.id}>
                  <TableCell>{li.description}</TableCell>
                  <TableCell className="tabular-nums">{li.quantity}</TableCell>
                  <TableCell className="text-muted-foreground">{li.unit_of_measure}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {li.source_pr_id ? (
                      <Link
                        to="/purchase-requests/$id"
                        params={{ id: li.source_pr_id }}
                        className="text-primary hover:underline"
                      >
                        {li.source_pr_number ?? "—"}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Attachments ownerType="rfqs" ownerId={id} />
    </div>
  );
}
